import React from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Calendar, ArrowRight, AlertCircle } from 'lucide-react';
import CTA from '../components/CTA';

const BladderUrgencyHypnotherapy: React.FC = () => {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Why do I suddenly need the toilet all the time?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Bladder urgency is not always a physical issue. The bladder and brain are closely connected through the nervous system. When the body is under pressure or the brain has learned to associate certain situations with urgency, the signals can become stronger and more frequent. This is why many people experience urgency even when tests are normal — the system has become over-alert.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can hypnotherapy help with bladder urgency?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Clinical hypnotherapy works by helping the nervous system settle and retraining the brain\'s response to bladder signals. Sessions focus on reducing the brain\'s anticipation response, calming the physical stress response, helping bladder signals return to a natural rhythm, and building confidence in difficult situations.',
        },
      },
      {
        '@type': 'Question',
        name: 'How many sessions are needed for bladder urgency?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A course of around 8 to 12 sessions is common for more established patterns, although many people start to notice changes earlier. Sessions typically begin weekly, gradually spacing out as the pattern improves.',
        },
      },
      {
        '@type': 'Question',
        name: 'Why doesn\'t bladder urgency settle on its own?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Once the brain has learned the urgency pattern, it can continue even when the original situation has passed. Managing through restricting fluids, going more often, or planning around toilets can actually reinforce the cycle. This isn\'t a lack of willpower — it\'s how the nervous system learns and protects.',
        },
      },
    ],
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Bladder Urgency Hypnotherapy',
    description: 'Clinical hypnotherapy for bladder urgency and frequent urination in Belfast and Northern Ireland. Helping the nervous system settle and retraining the brain\'s response to bladder signals.',
    provider: {
      '@type': 'Person',
      name: 'Sharon Clare',
      jobTitle: 'Clinical Hypnotherapist',
      url: 'https://www.sharonclare-hypnotherapy.com',
    },
    areaServed: [
      { '@type': 'City', name: 'Belfast' },
      { '@type': 'City', name: 'Lisburn' },
      { '@type': 'AdministrativeArea', name: 'Northern Ireland' },
    ],
    serviceType: 'Clinical Hypnotherapy',
  };

  return (
    <main>
      <Helmet>
        <title>Bladder Urgency &amp; Anxiety | Why Do I Need the Toilet All the Time? | Sharon Clare NI</title>
        <meta
          name="description"
          content="Bladder urgency and frequent urination explained. When medical tests are normal, hypnotherapy can help retrain the nervous system. Belfast, Lisburn &amp; online sessions with Sharon Clare."
        />
        <meta
          name="keywords"
          content="bladder urgency hypnotherapy, frequent urination anxiety, overactive bladder stress, nervous bladder Belfast, bladder urgency Northern Ireland, toilet anxiety hypnotherapy"
        />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/bladder-urgency-hypnotherapy/" />
        <meta property="og:title" content="Why Do I Suddenly Need the Toilet All the Time? | Bladder Urgency &amp; Anxiety" />
        <meta property="og:description" content="Bladder urgency and frequent urination can feel confusing — especially when tests are normal. Understand why it happens and how hypnotherapy can help." />
        <meta property="og:url" content="https://www.sharonclare-hypnotherapy.com/bladder-urgency-hypnotherapy/" />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom text-center max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-4">
            Why Do I Suddenly Need the Toilet All the Time?
          </h1>
          <p className="text-xl sm:text-2xl font-heading text-accent-600 mb-8">
            Bladder Urgency &amp; Anxiety Explained
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-8 max-w-3xl mx-auto">
            Bladder urgency and frequent urination can feel confusing and frustrating — especially when
            medical tests come back normal and there's no clear physical cause. Many people across
            Belfast and Northern Ireland experience a sudden or ongoing need to use the toilet, often
            linked to stress, anxiety, or changes in the nervous system. This is more common than you
            might think — and it is something that can change.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="btn-primary inline-flex items-center justify-center"
            >
              <Calendar className="w-5 h-5 mr-2" /> Book a Consultation
            </a>
            <a href="tel:07932139245" className="btn-secondary inline-flex items-center justify-center">
              Call 07932 139245
            </a>
          </div>
        </div>
      </section>

      {/* Does This Sound Familiar? */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
            Does This Sound Familiar?
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">You might recognise this pattern:</p>
          <ul className="space-y-4 mb-8">
            {[
              'Needing to know where the nearest toilet is at all times',
              'Going "just in case" — sometimes repeatedly',
              'Avoiding long journeys, meetings, or travel',
              'Choosing aisle seats or planning routes carefully',
              'Limiting how much you drink before going out',
              'Feeling anxious about being "caught short"',
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-accent-600 mt-0.5 shrink-0" />
                <span className="text-gray-700 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-gray-700 leading-relaxed">
            For many people, it starts in a very specific moment — and then gradually becomes
            something much bigger.
          </p>
        </div>
      </section>

      {/* Why This Happens */}
      <section className="py-16 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
            Why This Happens
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Bladder urgency is not always just a physical issue.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            The bladder and brain are closely connected through the nervous system. When the body is
            under pressure — or when the brain has learned to associate certain situations with urgency
            — the signals can become stronger and more frequent.
          </p>
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 mb-6">
            <p className="text-gray-800 text-lg leading-relaxed text-center italic">
              The brain begins to anticipate the need to go.<br />
              The body responds.<br />
              And a pattern develops.
            </p>
          </div>
          <p className="text-gray-700 leading-relaxed">
            This is why many people continue to experience urgency even when tests are normal. The
            system has become over-alert, rather than damaged.
          </p>
        </div>
      </section>

      {/* A Story That Might Feel Familiar */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
            A Story That Might Feel Familiar
          </h2>
          <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
            <p className="text-gray-700 leading-relaxed mb-4">
              A man I worked with — I'll call him James — came to see me after something that, on the
              surface, didn't seem like a big deal.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              He had been travelling through an airport and found himself in a situation where he wasn't
              sure he would reach a toilet in time. He did — but it unnerved him.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              From that point on, something changed.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              The next time he travelled, he noticed himself thinking about it more. He started going to the
              toilet repeatedly "just in case". He chose aisle seats. He limited how much he drank before
              flying.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              At first, it was just airports. Then it became longer journeys. Then meetings. Then everyday
              situations.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              What had been a one-off experience had become a pattern.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Not because anything was wrong physically — but because his brain had learned to anticipate
              a problem and was trying to stay one step ahead of it.
            </p>
            <p className="text-gray-700 leading-relaxed font-medium text-accent-700">
              This is a pattern I see regularly in practice — and once it's understood, it can be changed.
            </p>
          </div>
        </div>
      </section>

      {/* Why It Doesn't Just Settle */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
            Why It Doesn't Just Settle on Its Own
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Once the brain has learned this pattern, it can continue even when the original situation has
            passed.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            Trying to manage it through:
          </p>
          <ul className="space-y-3 mb-6">
            {[
              'Restricting fluids',
              'Going more often',
              'Planning around toilets',
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                <span className="text-gray-700 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-gray-700 leading-relaxed mb-4">
            can actually <strong>reinforce the cycle</strong>.
          </p>
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <p className="text-gray-800 leading-relaxed text-center">
              This isn't a lack of willpower.<br />
              <span className="font-medium">It's simply how the nervous system learns and protects.</span>
            </p>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
            How Hypnotherapy Helps
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Clinical hypnotherapy works by helping the nervous system settle and retraining the brain's
            response to these signals.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">In our sessions we focus on:</p>
          <ul className="space-y-4 mb-8">
            {[
              'Reducing the brain\'s anticipation response',
              'Calming the physical stress response in the body',
              'Helping the bladder signals return to a more natural rhythm',
              'Building confidence in situations that currently feel difficult',
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-accent-600 mt-0.5 shrink-0" />
                <span className="text-gray-700 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-gray-700 leading-relaxed mb-4">
            This is a structured, practical approach.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Most people begin to notice changes as the body becomes less reactive and the mind feels
            more in control.
          </p>
        </div>
      </section>

      {/* What Progress Looks Like */}
      <section className="py-16 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
            What Progress Looks Like
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            As the pattern changes, people often notice:
          </p>
          <ul className="space-y-4 mb-8">
            {[
              'Fewer urgent sensations',
              'Less need to plan around toilets',
              'More confidence when travelling or going out',
              'A general sense of calm around situations that used to feel stressful',
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-accent-600 mt-0.5 shrink-0" />
                <span className="text-gray-700 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-gray-700 leading-relaxed">
            The goal is not to "fight" the symptom, but to help the system return to a more balanced,
            steady state.
          </p>
        </div>
      </section>

      {/* My Experience */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
            My Experience
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            I work with clients from my Belfast clinic on the Ormeau Road (days and times vary, with early evening appointments available), my Lisburn clinic at The Arzen Collective, as well as online, supporting a range of
            mind-body symptoms where the nervous system plays a key role.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            Alongside private practice, I also support patients undergoing medical treatment through my
            work with the Cancer Centre at Belfast City Hospital and the Macmillan Support &amp;
            Information Centre.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            This has given me extensive experience in helping people manage anxiety responses in the
            body — particularly when the nervous system becomes over-sensitive or reactive.
          </p>
          <p className="text-gray-700 leading-relaxed font-medium">
            Bladder urgency is something I see regularly, and it is often far more common than people
            realise.
          </p>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
            What to Expect
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Bladder urgency patterns can take time to fully resolve, and everyone is different.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            Typically, we would begin with weekly sessions, gradually spacing these out as the pattern
            improves.
          </p>
          <p className="text-gray-700 leading-relaxed">
            A course of around 8-12 sessions is common for more established patterns, although many
            people start to notice changes earlier.
          </p>
        </div>
      </section>

      {/* In Practice */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
            In Practice
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            This is not something you have to manage forever.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            When the nervous system settles and the brain stops anticipating the problem, the signals
            begin to return to a more natural rhythm.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            For some people, this happens quite quickly. For others — particularly where the pattern has
            been in place for longer — it can take a little more time.
          </p>
          <p className="text-gray-700 leading-relaxed font-medium text-accent-700">
            But change is absolutely possible.
          </p>
        </div>
      </section>

      {/* FAQ Schema Section */}
      <section className="py-16 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-8">
            Common Questions
          </h2>
          <div className="space-y-6">
            {faqSchema.mainEntity.map(({ name, acceptedAnswer }) => (
              <div key={name} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-3">{name}</h3>
                <p className="text-gray-700 leading-relaxed">{acceptedAnswer.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pillar link */}
      <section className="py-8 bg-white">
        <div className="container-custom max-w-4xl text-center">
          <a href="/stress-physical-symptoms" className="text-accent-600 hover:text-accent-700 font-semibold inline-flex items-center gap-1">
            Learn more about stress-conditioned physical symptoms <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Next Step"
        subtitle="If you're experiencing bladder urgency or frequency and it's starting to affect your confidence or daily life, support is available. You don't need to have this fully figured out before reaching out — that's what I'm here to help you with."
        primaryButtonText="Book a Consultation"
        primaryButtonHref="/contact"
        secondaryButtonText="Call 07932 139245"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default BladderUrgencyHypnotherapy;
